import * as React from 'react';
import { MdAdd } from 'react-icons/md';
import { FaInfo } from 'react-icons/fa';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { FIELD_TYPE } from 'app/infrastructure/simple-form';
import { AddButton } from 'ui/common/button/add-button';
import { PanelDefaultValuesComponentProps } from './types';
import { readSelectionParams, simpleFormNames, checkFormValues } from './panel-default-values-utils';

import styles from './add-default-values.scss';
import { manageOptionsButton } from 'app/area/shared/manage-options';

export interface AddDefaultValueProps extends PanelDefaultValuesComponentProps {
  onSubmit: () => void;
}

export interface AddDefaultValueState {
  showModal: boolean;
  hasDefaultValueFields: boolean;
}

export class AddDefaultValue extends React.Component<AddDefaultValueProps, AddDefaultValueState> {
  public state: Readonly<AddDefaultValueState> = { showModal: false, hasDefaultValueFields: false };

  public componentDidUpdate(prevProps: AddDefaultValueProps) {
    if (this.props.formValues[simpleFormNames.values] !== prevProps.formValues[simpleFormNames.values]) {
      this.setState({
        hasDefaultValueFields: true
      }, () => this.setState({ hasDefaultValueFields: false }));
    }
  }

  private showAddDefaultValueModal = () => this.setState({ showModal: true });
  private hideAddDefaultValueModal = () => this.setState({ showModal: false });

  private onDone = () => {
    this.props.onSubmit();
    this.hideAddDefaultValueModal();
  };

  private isDisabled = () => {
    return checkFormValues([
      simpleFormNames.optionsCollections,
      simpleFormNames.caseTypes,
      simpleFormNames.values,
      simpleFormNames.valuesFields
    ], this.props.formValues) || (this.props.isUserTypeRequired && !this.props.userType) || !!this.props.validationErrors;
  };

  // eslint:disable-next-line:max-func-body-length
  public render(): JSX.Element {
    const { apiToUse, endpoints, translation, userType, isUserTypeRequired, formValues = {}, formOptions, onSubmit,
      fieldsOptionParams } = this.props;
    const {
      defaultValues: {
        optionsCollections,
        caseTypes,
        values,
        valuesFields
      }
    } = endpoints;

    const SimpleForm = this.props.formComponent;

    const selectionParams = readSelectionParams(this.props.formValues);
    const {
      data: {
        optionsColection,
        caseType
      }
    } = selectionParams;

    const optionsCollectionsValue = formValues && formValues[simpleFormNames.optionsCollections];
    const optionsCollectionSearchRequired = optionsCollectionsValue && optionsCollectionsValue.extra
      && optionsCollectionsValue.extra.SearchRequired;

    const hasDefaultValues = !!this.props.formValues.defaultValue_values && !this.state.hasDefaultValueFields;

    return <>
      <AddButton icon={MdAdd} onClick={this.showAddDefaultValueModal} required={false}>
        {translation.default_value_add_new}
      </AddButton>

      {this.state.showModal && <ModalWrapper
        headerTitle={translation.default_value_add_new}
        btnTitle={translation.add}
        btnActionTitle={translation.default_value_add_another}
        btnCancelTitle={translation.cancel}
        onClose={this.hideAddDefaultValueModal}
        onDone={this.onDone}
        onAction={onSubmit}
        closeOnDone={false}
        largeSize={false}
        isDisabled={this.isDisabled}
        canCloseOnBackground={false}>
        {(isUserTypeRequired && !userType) && <div className="col-lg-12">
          <div className={styles.disclaimerPanel}>
            <FaInfo /> {translation.add_default_value_disclaimer}
          </div>
        </div>}
        <div className="col-lg-12">
          <SimpleForm
            name={simpleFormNames.optionsCollections}
            type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
            optionsEndpoint={optionsCollections}
            placeholder={translation.search}
            apiToUse={apiToUse}
            translation={translation}
            descriptionAllLevels={true}
            label={{ text: translation.code_table, required: true }}
            initialValue={null} />
        </div>
        <div className="col-lg-12">
          <SimpleForm
            name={simpleFormNames.caseTypes}
            type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
            optionsEndpoint={caseTypes}
            placeholder={translation.search}
            apiToUse={apiToUse}
            translation={translation}
            descriptionAllLevels={true}
            initiallyShowLongDescription={false}
            label={{ text: translation.case_type, required: true }}
            initialValue={null} />
        </div>
        {optionsColection && caseType && <div className="col-lg-12">
          <SimpleForm
            name={simpleFormNames.values}
            type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
            optionsEndpoint={values(optionsColection && optionsColection.value)}
            optionsParams={{ caseTypeId: caseType && caseType.value }}
            placeholder={translation.search}
            apiToUse={apiToUse}
            translation={translation}
            descriptionAllLevels={true}
            label={{ text: translation.default_value, required: true }}
            initialValue={null}
            searchRequired={optionsCollectionSearchRequired}
            extraFooterButton={manageOptionsButton(translation.manage_options,
              formOptions?.options?.defaultValue_values?.value ?? optionsColection.value)}
          />
        </div>}
        {!this.props.isValueFieldsReadOnly(optionsColection, caseType, userType)
          && hasDefaultValues
          && <div className="col-lg-12">
            <SimpleForm
              name={simpleFormNames.valuesFields}
              type={FIELD_TYPE.SELECT_MULTIPLE}
              optionsEndpoint={valuesFields(optionsColection && optionsColection.value)}
              optionsParams={{
                caseTypeId: caseType && caseType.value,
                userTypeContext: userType,
                ...fieldsOptionParams
              }}
              placeholder={translation.search}
              apiToUse={apiToUse}
              translation={translation}
              descriptionAllLevels={true}
              label={{ text: translation.field, required: true }}
              initialValue={this.props.initialDefaultValueFields}
              displaySelectAll={true}
            />

            {!!this.props.validationErrors && this.props.validationErrors}
          </div>}
      </ModalWrapper>}
    </>;
  }
}
